import {CompactCommentButton} from '@github-ui/commenting/CompactCommentButton'
import {use, useCallback, useImperativeHandle, useRef, useState} from 'react'
import {AddCommentEditorNotDiffLines, type AddCommentEditorHandle} from '@github-ui/diff-lines/add-comment-editor'
import type {ResolutionReasonType, Thread} from '@github-ui/diff-lines/conversation-types'
import {useCreateReplyCommentMutation} from '../../mutations/use-create-reply-comment-mutation'
import {useResolveThreadMutation} from '../../mutations/use-resolve-thread-mutation'
import {getThreadMarkerKeys} from '../../helpers/thread-positioning'
import {clsx} from 'clsx'
import {CommentsContext} from './CommentsContext'
import {threadLabel} from './utils/thread-label'
import {InlineThreadResolveActions} from './InlineThreadResolveActions'

type SubmitReplyArgs = {
  commentText: string
  onCompleted?: () => void
  onError: (error: Error) => void
  submitBatch?: boolean
  resolveAfter?: boolean
}

// Plain module-scope helper (not a component or hook) that drives the reply — and optional resolve —
// with `mutateAsync`/`await`. Using the awaited promises (rather than a per-call `mutate` `onSuccess`)
// is what makes the resolve reliable: the reply mutation's own `onSuccess` invalidates the
// thread-previews query, which re-renders the Overview list and can unmount this composer. TanStack
// drops per-call `mutate` callbacks when the calling component unmounts, so a chained resolve there
// would silently never run. `mutateAsync` resolves independently of mount state.
async function submitReplyComment({
  args,
  createReplyComment,
  resolveThread,
  filePath,
  thread,
  onCompleted,
}: {
  args: SubmitReplyArgs
  createReplyComment: ReturnType<typeof useCreateReplyCommentMutation>['mutateAsync']
  resolveThread: ReturnType<typeof useResolveThreadMutation>['mutateAsync']
  filePath: string
  thread: Thread
  onCompleted?: () => void
}) {
  const {commentText, onCompleted: onCommentCompleted, onError, submitBatch, resolveAfter} = args

  const inReplyTo = thread.commentsData?.comments.at(-1)?.databaseId
  if (!inReplyTo) {
    onError(new Error('Missing reply target comment'))
    return
  }

  try {
    await createReplyComment({text: commentText, inReplyTo, submitBatch: submitBatch ?? false, path: filePath})
  } catch (error) {
    onError(error instanceof Error ? error : new Error('Failed to submit reply'))
    return
  }

  // The reply comment succeeded, so always run the completion callbacks to clear the draft and close
  // the composer. Handling this independently of the resolve step means a failed resolve can't leave
  // the draft behind — which would otherwise let the user resubmit and post a duplicate reply.
  onCommentCompleted?.()
  onCompleted?.()

  if (resolveAfter) {
    // Resolve is best-effort: the reply is already saved and the composer is closed, so a resolve
    // failure must not surface through `onError` (that re-persists the draft) or roll back the reply.
    try {
      await resolveThread({threadId: thread.id})
    } catch {
      // Intentionally ignored — the reply succeeded; a follow-up resolve failure is non-fatal.
    }
  }
}

export interface ThreadReplyComposerHandle {
  insertQuote: (quotedText: string) => void
}

export function ThreadReplyComposer({
  thread,
  batchingEnabled = true,
  startExpanded = false,
  replyAndResolveEnabled = false,
  inlineResolutionEnabled = false,
  onCompleted,
  className,
  ref,
}: {
  thread: Thread
  startExpanded?: boolean
  replyAndResolveEnabled?: boolean
  inlineResolutionEnabled?: boolean
  onCompleted?: () => void
  className?: string
  batchingEnabled?: boolean
  ref?: React.Ref<ThreadReplyComposerHandle>
}) {
  const {filePath, lineKey} = getThreadMarkerKeys(thread)
  const {contextualCommentControls} = use(CommentsContext)
  const {mutateAsync: createReplyComment, isPending: isCreatingReply} = useCreateReplyCommentMutation(filePath, lineKey)
  const {
    mutate: resolveThreadMutation,
    mutateAsync: resolveThread,
    isPending: isResolving,
  } = useResolveThreadMutation(filePath, lineKey)
  const [isReplying, setIsReplying] = useState(startExpanded)
  const [pendingQuote, setPendingQuote] = useState<string | undefined>(undefined)
  const actionPending = isCreatingReply || isResolving
  const composerRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<AddCommentEditorHandle>(null)

  const handleSubmitReply = useCallback(
    (args: SubmitReplyArgs) => {
      void submitReplyComment({args, createReplyComment, resolveThread, filePath, thread, onCompleted})
    },
    [createReplyComment, resolveThread, filePath, thread, onCompleted],
  )

  const handleResolve = (reason?: ResolutionReasonType) => {
    resolveThreadMutation({threadId: thread.id, resolutionReason: reason})
  }

  useImperativeHandle(
    ref,
    () => ({
      insertQuote(text) {
        if (isReplying && editorRef.current) {
          editorRef.current.insertQuote(text, {append: true})
          return
        } else {
          // Editor is unmounted; seed it via the `quotedText` prop and expand so it mounts with the
          // quoted markdown as its initial value.
          setPendingQuote(text)
          setIsReplying(true)
        }
      },
    }),
    [isReplying],
  )

  return (
    <div className={clsx('p-2 bgColor-inset', className)} ref={composerRef}>
      {isReplying ? (
        <AddCommentEditorNotDiffLines
          ref={editorRef}
          batchingEnabled={batchingEnabled}
          replyAndResolveEnabled={replyAndResolveEnabled}
          externalActionPending={actionPending}
          renderAdditionalActions={
            inlineResolutionEnabled
              ? footer => (
                  <InlineThreadResolveActions
                    thread={thread}
                    busy={actionPending}
                    onResolve={handleResolve}
                    footer={footer}
                  />
                )
              : undefined
          }
          condensed
          focusOnMount
          filePath={filePath}
          isReplying
          quotedText={pendingQuote}
          threadId={thread.id}
          onAddComment={handleSubmitReply}
          onCancelComment={() => {
            setIsReplying(false)
            setPendingQuote(undefined)
          }}
        />
      ) : (
        <div className="d-flex flex-items-center gap-2">
          <CompactCommentButton
            aria-label={contextualCommentControls ? `Write a reply in ${threadLabel(thread)}` : undefined}
            onClick={() => setIsReplying(true)}
          >
            Write a reply
          </CompactCommentButton>
          {inlineResolutionEnabled && (
            <InlineThreadResolveActions thread={thread} busy={actionPending} onResolve={handleResolve} />
          )}
        </div>
      )}
    </div>
  )
}
